/*
 * Project: Goalscape
 * File:    CreateWorkspaceModal.js
 * Path:    src/components/CreateWorkspaceModal.js
 * Description: Modal component for creating a new workspace.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement form validation and error handling for a better user experience.
 */
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createWorkspace } from '../utils/workspaceUtils';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import WorkspaceForm from './workspace/WorkspaceForm';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
} from '../styles/CreateWorkspaceModal.styles';

const CreateWorkspaceModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    useBodyScrollLock();

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await createWorkspace(formData, user);
            onClose();
        } catch (error) {
            console.error('Error creating workspace:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>Create Workspace</ModalTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>

                <WorkspaceForm
                    name={formData.name}
                    description={formData.description}
                    loading={loading}
                    onNameChange={handleChange}
                    onDescriptionChange={handleChange}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                />
            </ModalContent>
        </ModalOverlay>
    );
};

export default CreateWorkspaceModal;